import {
  resolvePassView,
  validateWalletPassDesign,
  type BrowserWalletPassView,
  type WalletBindingValues,
} from "@lemmonhq/blogic/wallet-design";
import { getRewardText } from "@lemmonhq/orders";
import type {
  WalletPassDesignDocument,
  WalletSelectedDesign,
} from "@lemmonhq/types";
import type { CollectedProgram, CollectedTier } from "@app/server/client";
import type { MembershipWalletPass } from "@app/server/trpc/routers/wallet-passes";

/**
 * The Collected wallet draws every card from its program's selected design,
 * parsed with the shared schema. A design that fails to parse is reported and
 * shown as unavailable — never swapped for a generic look.
 */

const parsed = new WeakMap<
  WalletSelectedDesign,
  WalletPassDesignDocument | null
>();

const parseDesign = (
  kind: "coin" | "membership",
  selected: WalletSelectedDesign,
): WalletPassDesignDocument | null => {
  if (parsed.has(selected)) return parsed.get(selected)!;
  const result = validateWalletPassDesign(kind, selected.document);
  if (!result.success) {
    console.error(
      `[collected] wallet design ${selected.id} is invalid`,
      result.issues,
    );
  }
  const document = result.success ? result.document : null;
  parsed.set(selected, document);
  return document;
};

/** Whether the design shows this pass in Collected. Unparseable designs stay
 * visible (as an unavailable card) so a customer never loses sight of coins. */
export const passBrowserEnabled = (
  kind: "coin" | "membership",
  selected: WalletSelectedDesign | null | undefined,
): boolean =>
  selected ? (parseDesign(kind, selected)?.browser.enabled ?? true) : true;

/** Whether the design offers a wallet platform's install badge. */
export const passPlatformEnabled = (
  kind: "coin" | "membership",
  selected: WalletSelectedDesign | null | undefined,
  platform: "apple" | "google",
): boolean =>
  selected ? (parseDesign(kind, selected)?.[platform].enabled ?? false) : false;

/** The artwork a pass's design selects, for place stacks and reveals. */
export const passArtworkUrl = (
  kind: "coin" | "membership",
  selected: WalletSelectedDesign | null | undefined,
): string | null => {
  if (!selected) return null;
  const ref = parseDesign(kind, selected)?.brand.assets.artwork;
  return ref ? (selected.asset_urls[ref.asset_id] ?? null) : null;
};

export const passBrowserView = (
  kind: "coin" | "membership",
  selected: WalletSelectedDesign | null | undefined,
  bindings: WalletBindingValues,
  locale: string,
): BrowserWalletPassView | null => {
  const document = selected ? parseDesign(kind, selected) : null;
  if (!selected || !document) return null;
  return resolvePassView(
    { kind, document, bindings, assetUrls: selected.asset_urls, locale },
    "browser",
  );
};

type Translate = (key: string, params?: (string | number)[]) => string;

/** Coin-program binding values, matching the tokens DBOS supplies. */
export const coinCardBindings = (
  program: CollectedProgram,
  shopName: string,
  tier: CollectedTier | null,
  options: { balance: number; t: Translate },
): WalletBindingValues => ({
  "coins.balance": options.balance,
  "coins.required": tier?.amount ?? null,
  "coins.label": program.coin_label,
  "reward.text": tier
    ? getRewardText(tier.reward, options.t as any, {
        coinCost: tier.amount,
        coinLabel: program.coin_label,
      })
    : null,
  "coin_program.name": program.name,
  "restaurant.name": shopName,
});

/** Membership binding values, matching the tokens DBOS supplies. */
export const membershipCardBindings = (
  pass: MembershipWalletPass,
  rewardText: string | null,
): WalletBindingValues => ({
  "customer.status_level": "Active",
  "membership.expires_at": pass.expires_at,
  "membership_program.name": pass.program_name,
  "reward.text": rewardText,
  "restaurant.name": pass.place_name,
});
